import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import filterIcon from "../../assets/icons/filter-icon.svg";
import Button from "../ui/Button";

const POPUP_WIDTH = 300;
const POPUP_GAP = 6;

function ColumnFilter({ column }) {
    const { filterValue, setFilter } = column;
    const title =
        typeof column.Header === "string" ? column.Header : column.id;
    const isFiltered = filterValue !== undefined && filterValue !== "";

    const [isOpen, setIsOpen] = useState(false);
    const [value, setValue] = useState("");
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const popupRef = useRef(null);
    const inputRef = useRef(null);

    const close = useCallback((restoreFocus = true) => {
        setIsOpen(false);
        if (restoreFocus) buttonRef.current?.focus();
    }, []);

    const open = (e) => {
        e.stopPropagation();
        if (isOpen) {
            close(false);
            return;
        }
        const rect = buttonRef.current.getBoundingClientRect();
        const maxLeft = window.innerWidth - POPUP_WIDTH - 10;
        setPosition({
            top: rect.bottom + window.scrollY + POPUP_GAP,
            left: Math.max(10, Math.min(rect.left + window.scrollX, maxLeft)),
        });
        setValue(isFiltered ? String(filterValue) : "");
        setIsOpen(true);
    };

    useEffect(() => {
        if (!isOpen) return undefined;
        inputRef.current?.focus();
        const handleMouseDown = (event) => {
            if (
                !popupRef.current?.contains(event.target) &&
                !buttonRef.current?.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, [isOpen]);

    const apply = () => {
        const trimmed = value.trim();
        setFilter(trimmed === "" ? undefined : trimmed);
        close();
    };

    const clear = () => {
        setFilter(undefined);
        setValue("");
        close();
    };

    const handleKeyDown = (e) => {
        e.stopPropagation();
        if (e.key === "Escape") close();
        if (e.key === "Enter" && value.trim() !== (filterValue ?? "")) {
            apply();
        }
    };

    return (
        <>
            <button
                type="button"
                ref={buttonRef}
                onClick={open}
                aria-label={`Column filter for ${title}`}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                data-filter-icon
                style={{
                    marginLeft: 2,
                    cursor: "pointer",
                    position: "relative",
                    background: "none",
                    border: "none",
                    padding: 0,
                }}
            >
                {isFiltered && (
                    <i
                        className="fa-solid fa-circle-check"
                        style={{
                            color: "#5E9E42",
                            position: "absolute",
                            top: -6,
                            left: 7,
                        }}
                    />
                )}
                <img src={filterIcon} alt="" />
            </button>
            {isOpen &&
                ReactDOM.createPortal(
                    <div
                        ref={popupRef}
                        className="filter-popup-box section-card"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="column-filter-title"
                        style={{
                            top: position.top,
                            left: position.left,
                            width: POPUP_WIDTH,
                            zIndex: 1000,
                        }}
                        onKeyDown={handleKeyDown}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="top-line">
                            <div className="title" id="column-filter-title">
                                <img src={filterIcon} alt="" />
                                <label>Filter {title}</label>
                            </div>
                            <div
                                className="close-area"
                                tabIndex={0}
                                role="button"
                                aria-label="Close filter popup"
                                onClick={() => close()}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        close();
                                    }
                                }}
                            >
                                <span className="close-text">Close</span>
                            </div>
                        </div>
                        <div className="filter-box">
                            <i
                                className="fa fa-search search"
                                aria-hidden="true"
                            ></i>
                            <input
                                ref={inputRef}
                                type="text"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder="Filter"
                                className="input-box"
                                aria-label={`Filter input for ${title}`}
                            />
                        </div>
                        <div
                            className="d-flex"
                            style={{ justifyContent: "flex-end", gap: 8, marginTop: 16 }}
                        >
                            {isFiltered && (
                                <Button
                                    text="Clear"
                                    outline={true}
                                    onClick={clear}
                                />
                            )}
                            <Button
                                text="Apply"
                                onClick={apply}
                                disabled={
                                    !value.trim() ||
                                    value.trim() === (filterValue ?? "")
                                }
                            />
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
}

ColumnFilter.propTypes = {
    column: PropTypes.shape({
        id: PropTypes.string,
        Header: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        filterValue: PropTypes.any,
        setFilter: PropTypes.func.isRequired,
    }).isRequired,
};

export default ColumnFilter;
